import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Copy, Trash2, Eye, Loader2 } from "lucide-react";
import { api, asset, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { pickProfile } from "./Customize";
import { PageHead, Locked } from "./parts";

export default function ImageHost() {
  const { user, setUser } = useAuth();
  const owned = (user.purchases || []).includes("image_host");
  const [imgs, setImgs] = useState([]);
  const [domains, setDomains] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const ref = useRef(null);
  const load = () => api.get("/me/images").then(({ data }) => setImgs(data));
  useEffect(() => { if (owned) { load(); api.get("/domains").then(({ data }) => setDomains(data)); } }, [owned]);

  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post("/uploads?kind=host", fd); } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setBusy(false); load(); toast.success("Upload complete");
  };
  const shareUrl = (id) => `${window.location.origin}/i/${id}`;
  const display = (id) => `${user.image_domain || window.location.host}/i/${id}`;
  const copy = (id) => { navigator.clipboard.writeText(shareUrl(id)); toast.success("Link copied"); };
  const del = async (id) => { await api.delete(`/me/images/${id}`); setImgs((x) => x.filter((i) => i.id !== id)); toast.success("Deleted"); };
  const setDomain = async (v) => {
    try { const { data } = await api.put("/me/profile", { ...pickProfile(user), image_domain: v }); setUser(data); toast.success("Image domain updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!owned) return (<div><PageHead eyebrow="image host" title="host your images" /><Locked what="image hosting">Upload images and share them with short links on your own alias domain — €3.99 lifetime.</Locked></div>);

  return (
    <div>
      <PageHead eyebrow="image host" title="your images">
        <select data-testid="image-domain-select" className="field w-56" value={user.image_domain || ""} onChange={(e) => setDomain(e.target.value)}>
          <option value="">default domain</option>
          {domains.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </PageHead>
      <div data-testid="image-dropzone" onClick={() => ref.current.click()} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); upload([...e.dataTransfer.files]); }}
        className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-14 text-center transition-colors ${drag ? "border-[#ff3b91] bg-[#ff3b91]/5" : "border-[#262630] hover:border-[#ff3b91]/50"}`}>
        {busy ? <Loader2 className="h-7 w-7 animate-spin text-[#ff3b91]" /> : <Upload className="h-7 w-7 text-[#ff3b91]" />}
        <div className="text-sm text-zinc-300">drop images or click to upload</div>
        <div className="font-mono text-[11px] text-zinc-600">png · jpg · gif · webp — 10MB max</div>
        <input ref={ref} type="file" accept="image/*" multiple hidden onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid="image-host-file-input" />
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="image-grid">
        {imgs.length === 0 && <div className="text-sm text-zinc-500">no images yet.</div>}
        {imgs.map((i) => (
          <div key={i.id} data-testid={`image-card-${i.id}`} className="group panel overflow-hidden transition-colors hover:border-[#ff3b91]/40">
            <a href={shareUrl(i.id)} target="_blank" rel="noreferrer" className="block aspect-video overflow-hidden bg-black"><img src={asset(i.url)} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></a>
            <div className="flex items-center gap-2 p-3">
              <div className="min-w-0 flex-1 truncate font-mono text-[11px] text-zinc-400">{display(i.id)}</div>
              <span className="flex items-center gap-1 font-mono text-[10px] text-zinc-600"><Eye className="h-3 w-3" />{i.views}</span>
              <button onClick={() => copy(i.id)} className="text-zinc-400 hover:text-white" data-testid={`image-copy-${i.id}`}><Copy className="h-4 w-4" /></button>
              <button onClick={() => del(i.id)} className="text-zinc-400 hover:text-red-400" data-testid={`image-delete-${i.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
